import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Clock } from "@phosphor-icons/react";
import { BUSINESS, HOURS } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dayIdx = { 0: "Sun", 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat" };

const field = "w-full bg-transparent border border-steel px-4 py-3 font-body text-bone placeholder:text-bone/35 focus:border-oxblood focus:outline-none transition-colors duration-200 rounded-none";

export default function Contact() {
  const today = dayIdx[new Date().getDay()];
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative bg-ink/70 py-24 md:py-36" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-brass">/ Come See Us</span>
        <h2 className="mt-5 mb-14 font-display uppercase leading-[0.9] tracking-tighter text-5xl md:text-7xl">
          Visit The <span className="text-oxblood">Shop</span>
        </h2>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Info + hours + map */}
          <div className="flex flex-col gap-8">
            <div className="grid sm:grid-cols-2 gap-4">
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(BUSINESS.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-address"
                className="border border-steel p-6 hover:border-brass transition-colors duration-200 group"
              >
                <MapPin size={26} className="text-oxblood mb-4" weight="fill" />
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone/45 mb-2">Location</div>
                <div className="font-body text-bone group-hover:text-brass transition-colors">{BUSINESS.address}</div>
              </a>
              <a
                href={BUSINESS.phoneHref}
                data-testid="contact-phone"
                className="border border-steel p-6 hover:border-brass transition-colors duration-200 group"
              >
                <Phone size={26} className="text-oxblood mb-4" weight="fill" />
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone/45 mb-2">Call Us</div>
                <div className="font-body text-bone group-hover:text-brass transition-colors">{BUSINESS.phone}</div>
              </a>
            </div>

            <div className="border border-steel p-6">
              <div className="flex items-center gap-3 mb-5">
                <Clock size={22} className="text-oxblood" weight="fill" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone/45">Hours</span>
              </div>
              <ul>
                {HOURS.map((h) => {
                  const isToday = h.day === today;
                  return (
                    <li
                      key={h.day}
                      data-testid={`hours-${h.day}`}
                      className={`flex items-center justify-between py-2.5 border-t border-steel/60 font-mono text-sm ${
                        isToday ? "text-brass" : "text-bone/70"
                      }`}
                    >
                      <span className="uppercase tracking-[0.15em] flex items-center gap-2">
                        {h.day}
                        {isToday && <span className="text-[9px] bg-oxblood text-bone px-2 py-0.5">TODAY</span>}
                      </span>
                      <span>{h.open} – {h.close}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="border border-steel h-64 overflow-hidden">
              <iframe
                title="N.P. GunSmith & Sales location"
                src={BUSINESS.mapEmbed}
                className="w-full h-full grayscale contrast-125"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="contact-form"
            className="border border-steel p-7 md:p-10 flex flex-col gap-5 bg-coal"
          >
            <p className="font-serif italic text-2xl text-bone/80 leading-snug">
              Have a question about an order, a repair, or a transfer? Drop us a line.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="contact-name" className={field} placeholder="Name *" value={form.name} onChange={set("name")} />
              <input data-testid="contact-email" type="email" className={field} placeholder="Email *" value={form.email} onChange={set("email")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="contact-phone-input" className={field} placeholder="Phone" value={form.phone} onChange={set("phone")} />
              <select data-testid="contact-subject" className={`${field} appearance-none cursor-pointer`} value={form.subject} onChange={set("subject")}>
                <option className="bg-ink">General Inquiry</option>
                <option className="bg-ink">Firearm Sales</option>
                <option className="bg-ink">Gunsmithing / Repair</option>
                <option className="bg-ink">Custom Build</option>
                <option className="bg-ink">FFL Transfer</option>
              </select>
            </div>
            <textarea data-testid="contact-message" rows={5} className={`${field} resize-none`} placeholder="Your message *" value={form.message} onChange={set("message")} />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="font-mono text-xs uppercase tracking-[0.2em] bg-oxblood text-bone px-8 py-4 hover:bg-brass hover:text-ink transition-colors duration-200 disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send Message"}
            </button>
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-bone/35">
              We respond during business hours. For urgent needs, call the shop.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
